import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  LogIn,
  Loader2,
  LogOut,
  Mail,
  CalendarCheck,
  MessageCircle,
  Inbox,
  Copy,
  RefreshCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "iariya_admin_token";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginCard = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Welcome back!");
      onLoggedIn(res.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--cream)] confetti-bg px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border-2 border-ink rounded-3xl shadow-brutal-lg p-8 sm:p-10"
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl border-2 border-ink bg-[var(--pink)] text-white flex items-center justify-center">
            <Inbox size={22} />
          </div>
          <div>
            <p className="uppercase text-xs font-bold tracking-[0.25em] text-stone-500">
              iAriya AI
            </p>
            <h1 className="font-display text-2xl font-black leading-tight">
              Admin sign-in
            </h1>
          </div>
        </div>

        <p className="mt-6 text-stone-700">
          Enter the admin password to view contact submissions and bookings.
        </p>

        <label className="block text-sm font-bold mt-6 mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          autoFocus
          placeholder="•••••••••"
          className="w-full border-2 border-ink rounded-xl px-4 py-3 bg-[var(--paper)] focus:outline-none focus:bg-[var(--yellow)]/30 font-medium"
        />

        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="btn-ink mt-6 w-full inline-flex items-center justify-center gap-2 bg-[var(--pink)] text-white px-5 py-3 disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Signing in…
            </>
          ) : (
            <>
              <LogIn size={18} /> Sign in
            </>
          )}
        </button>

        <a
          href="/"
          className="mt-6 block text-center text-sm text-stone-600 hover:text-[var(--pink)] font-semibold"
          data-testid="admin-login-back"
        >
          ← Back to site
        </a>
      </form>
    </div>
  );
};

const Stat = ({ label, value, color, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className={`border-2 border-ink rounded-3xl p-6 shadow-brutal ${color}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest opacity-80">
          {label}
        </p>
        <p className="font-display text-5xl font-black mt-2 leading-none">
          {value}
        </p>
      </div>
      <div className="h-11 w-11 rounded-xl border-2 border-ink bg-white text-ink flex items-center justify-center">
        <Icon size={20} />
      </div>
    </div>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("contacts");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/overview`, {
        headers: { "X-Admin-Token": token },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        onLogout();
      } else {
        toast.error("Couldn't load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      {/* Top bar */}
      <header className="border-b-2 border-ink bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl border-2 border-ink bg-[var(--pink)] text-white flex items-center justify-center font-display font-black">
              iA
            </div>
            <div>
              <p className="font-display text-xl font-black leading-tight">
                iAriya AI · Admin
              </p>
              <p className="text-xs text-stone-600">
                Submissions & Bookings dashboard
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="btn-ink inline-flex items-center gap-2 bg-white text-ink px-4 py-2 text-sm"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <a
              href="/"
              className="btn-ink hidden sm:inline-flex items-center gap-2 bg-[var(--yellow)] text-ink px-4 py-2 text-sm"
              data-testid="admin-view-site"
            >
              View site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="btn-ink inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {loading && !data ? (
          <div className="flex items-center justify-center py-32 text-stone-600">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : data ? (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Stat
                label="Contact submissions"
                value={data.contact_count}
                color="bg-[var(--pink)] text-white"
                icon={Mail}
                testid="admin-stat-contacts"
              />
              <Stat
                label="Bookings tracked"
                value={data.booking_count}
                color="bg-[var(--blue)] text-white"
                icon={CalendarCheck}
                testid="admin-stat-bookings"
              />
              <Stat
                label="AI chat sessions"
                value={data.chat_session_count}
                color="bg-[var(--yellow)] text-ink"
                icon={MessageCircle}
                testid="admin-stat-chats"
              />
            </div>

            {/* Tabs */}
            <div className="mt-10 flex gap-2 border-b-2 border-ink">
              {[
                {
                  key: "contacts",
                  label: `Contacts (${data.contact_count})`,
                  testid: "admin-tab-contacts",
                },
                {
                  key: "bookings",
                  label: `Bookings (${data.booking_count})`,
                  testid: "admin-tab-bookings",
                },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={t.testid}
                  className={`px-5 py-3 font-display font-extrabold border-2 border-ink border-b-0 rounded-t-xl -mb-[2px] ${
                    tab === t.key
                      ? "bg-[var(--pink)] text-white"
                      : "bg-white text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {tab === "contacts" && (
              <div
                data-testid="admin-contacts-panel"
                className="bg-white border-2 border-ink rounded-3xl rounded-tl-none shadow-brutal mt-0 overflow-hidden"
              >
                {data.contacts.length === 0 ? (
                  <p className="p-10 text-center text-stone-600">
                    No submissions yet.
                  </p>
                ) : (
                  <div className="divide-y-2 divide-stone-900">
                    {data.contacts.map((c) => (
                      <div key={c.id} className="p-6 hover:bg-[var(--cream)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-lg font-black">
                              {c.name}
                            </p>
                            <button
                              className="text-sm text-[var(--blue)] font-semibold inline-flex items-center gap-1.5 hover:underline"
                              onClick={() => copy(c.email)}
                              data-testid={`admin-contact-email-${c.id}`}
                            >
                              {c.email} <Copy size={12} />
                            </button>
                          </div>
                          <div className="text-right">
                            <span className="inline-block px-3 py-1 bg-[var(--yellow)] border-2 border-ink rounded-full text-xs font-bold">
                              {c.business_type}
                            </span>
                            <p className="text-xs text-stone-600 mt-2">
                              {fmtDate(c.timestamp)}
                            </p>
                          </div>
                        </div>
                        <p className="mt-3 text-stone-700 whitespace-pre-wrap">
                          {c.message}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "bookings" && (
              <div
                data-testid="admin-bookings-panel"
                className="bg-white border-2 border-ink rounded-3xl rounded-tl-none shadow-brutal mt-0 overflow-hidden"
              >
                {data.bookings.length === 0 ? (
                  <p className="p-10 text-center text-stone-600">
                    No bookings tracked yet. Real bookings will appear here the
                    moment someone completes a Calendly invite.
                  </p>
                ) : (
                  <div className="divide-y-2 divide-stone-900">
                    {data.bookings.map((b) => (
                      <div key={b.id} className="p-6 hover:bg-[var(--cream)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-lg font-black">
                              {b.event_type}
                            </p>
                            <p className="text-xs text-stone-600 mt-1 break-all">
                              {b.referrer || "Direct"}
                            </p>
                          </div>
                          <p className="text-xs text-stone-600">
                            {fmtDate(b.timestamp)}
                          </p>
                        </div>
                        {b.payload && Object.keys(b.payload).length > 0 && (
                          <pre className="mt-3 bg-[var(--paper)] border-2 border-ink rounded-xl p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(b.payload, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  return (
    <>
      {token ? (
        <Dashboard token={token} onLogout={logout} />
      ) : (
        <LoginCard onLoggedIn={setToken} />
      )}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            border: "2px solid #1c1917",
            borderRadius: "16px",
            boxShadow: "4px 4px 0 0 #1c1917",
            fontFamily: "Figtree, sans-serif",
            fontWeight: 600,
          },
        }}
      />
    </>
  );
}
